import RightPanelCard from "components/CommonComponents/RightPanelCard";
import {
  SELECT_CUSTOM_STYLES,
  LessonTypeColor,
} from "constants/CommonConstants";
import React, { useState } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Collapse,
  Row,
  Col,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Badge,
  CardFooter,
  Modal,
  ModalHeader,
  ModalBody,
  Popover,
  PopoverBody,
} from "reactstrap";
import Select from "react-select";
import { COURSE_TYPE, MODULES, LESSON_TYPE_META } from "components/constant";
import ReactQuill from "react-quill";

const initialFields = {
  title: {
    value: "",
    error: "",
  },
  descriptions: {
    value: "",
    error: "",
  },
  courseType: {
    value: "",
    error: "",
  },
  duration: {
    value: "",
    error: "",
  },
  sequence: {
    value: "",
    error: "",
  },
  files: {
    value: [],
    error: "",
  },
};

export const lessons = [
  {
    id: 1,
    title: "Lesson 1: Cable Jointing Basics",
    type: "VIDEO",
    duration: "10 min",
    description:
      "This introductory video covers the importance of proper cable jointing in low-voltage systems, common applications, and an overview of tools used in the trade.",
    color: "success",
  },
  {
    id: 2,
    title: "Lesson 2: Personal Protective Equipment",
    type: "VIDEO",
    duration: "10 min",
    description:
      "Procedures for ensuring safe cable jointing environments and correct usage of PPE.",
    color: "danger",
  },
  {
    id: 3,
    title: "Lesson 3: Cable Preparation Techniques",
    type: "PPT",
    duration: "10 min",
    description:
      "Step-by-step instructions for preparing LV cables for jointing.",
    color: "info",
  },
  {
    id: 4,
    title: "Lesson 4: Safety Guidelines & Best Practices",
    type: "VIDEO",
    duration: "20 min",
    description:
      "Learn essential safety protocols, hazard prevention, and compliance standards.",
    color: "danger",
  },
  {
    id: 5,
    title: "Lesson 5: Cable Jointing Practical Demonstration",
    type: "PDF",
    duration: "25 min",
    description:
      "A complete hands-on demonstration of LV cable jointing techniques in real-world scenarios.",
    color: "success",
  },
];

const LessonsSection = ({ open, toggle }) => {
  const [lessonFormData, setLessonFormData] = useState(initialFields);
  const [isModalOpen, setIsModalOpen] = useState(null); //"add "edit" "null"
  const [preview, setPreview] = useState({
    open: false,
    file: null,
  });
  const [hoverPopovers, setHoverPopovers] = useState(null);

  const openLessonPreview = (lesson) => {
    const meta = LESSON_TYPE_META[lesson.type];
    if (!meta) return;

    setPreview({
      open: true,
      file: {
        preview: meta.preview,
        previewType: meta.previewType,
        name: lesson.title,
      },
    });
  };

  const closePreview = () => {
    setPreview({
      open: false,
      file: null,
    });
  };

  const openAddModal = () => {
    setIsModalOpen("add");
    setLessonFormData(initialFields);
  };

  const closeAddModal = () => {
    setIsModalOpen(null);
    setLessonFormData({
      ...initialFields,
    });
  };

  const handleInputChange = (field, value) => {
    setLessonFormData((prev) => ({
      ...prev,
      [field]: { value, error: "" },
    }));
  };

  const handleOnBlur = (name, value) => {
    const trimmedValue = typeof value === "string" ? value.trim() : value;
    const error = getValidationError(name, trimmedValue);
    setLessonFormData((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        value: trimmedValue,
        error,
      },
    }));
  };

  const handleEditLesson = (lesson) => {
    setIsModalOpen("edit");
    setLessonFormData({
      title: { value: lesson.title || "", error: "" },
      descriptions: { value: lesson.description || "", error: "" },
      courseType: { value: lesson.type || "", error: "" },
      duration: { value: lesson.duration || "", error: "" },
      sequence: { value: lesson.id || "", error: "" },
      files: { value: [], error: "" },
    });
  };

  const validateForm = () => {
    let newLesson = { ...lessonFormData };

    //get all the keys in array
    Object.keys(newLesson).forEach((key) => {
      newLesson[key] = {
        ...newLesson[key],
        error: getValidationError(key, newLesson[key].value),
      };
    });
    setLessonFormData(newLesson); //if error set error state
    return Object.values(newLesson).every((item) => item.error === "");
  };

  const getValidationError = (name, value) => {
    const trimmedValue = typeof value === "string" ? value.trim() : value;
    switch (name) {
      case "title":
        return !trimmedValue ? "Please enter lesson title" : "";

      case "courseType":
        return !value ? "Please select lesson file upload  type" : "";

      case "sequence":
        return !value ? "Please enter content sequence" : "";

      case "duration":
        return !value ? "Please enter lesson duration" : "";

      case "descriptions":
        return !value ? "Please enter a course description " : "";

      case "files":
        return !value || value.length === 0
          ? "Please upload the lesson file"
          : "";

      default:
        return "";
    }
  };

  const handleSave = () => {
    if (!validateForm()) return;
  };

  const handleAddFile = (file) => {
    if (!file) return;
    const newFile = {
      file,
      preview: URL.createObjectURL(file),
      type: file.type,
      name: file.name,
    };
    setLessonFormData((prev) => ({
      ...prev,
      files: {
        value: [...prev.files.value, newFile],
        error: "",
      },
    }));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleAddFile(file);
  };

  const handleInputFileChange = (e) => {
    const file = e.target.files[0];
    if (file) handleAddFile(file);
    e.target.value = "";
  };

  const handleRemoveFile = (index) => {
    setLessonFormData((prev) => ({
      ...prev,
      files: {
        value: prev.files.value.filter((_, i) => i !== index),
        error: "",
      },
    }));
  };
  const handleDeleteLesson = (lessonTitle) => {
    // Remove the lesson from the lessons array
    const filteredLessons = lessons.filter(
      (lesson) => lesson.title !== lessonTitle,
    );
    closeAddModal();
  };

  const getFileMeta = (file) => {
    const type = file.type || "";

    if (type.startsWith("video/")) {
      return { icon: "fa-solid fa-circle-play", previewType: "video" };
    }

    if (type === "application/pdf") {
      return { icon: "fa-file-pdf", previewType: "pdf" };
    }

    if (
      type === "application/vnd.ms-powerpoint" ||
      type ===
        "application/vnd.openxmlformats-officedocument.presentationml.presentation"
    ) {
      return { icon: "fa-file-powerpoint", previewType: "ppt" };
    }

    if (type.startsWith("image/")) {
      return { icon: "fa-file-image", previewType: "image" };
    }

    return { icon: "fa-file", previewType: "unknown" };
  };

  const toggleLessonPopover = (index) =>
    setHoverPopovers(hoverPopovers === index ? null : index);

  return (
    <>
      <Card className="mb-3">
        <CardHeader>
          <div
            className="d-flex align-items-center cursor"
            onClick={() => toggle("lessons")}
          >
            <i className={`fa fa-chevron-${open.lessons ? "up" : "down"}`} />
            <h5 className="ml-2 mb-0 section-header-title">Lessons</h5>
          </div>
        </CardHeader>

        <Collapse isOpen={open.lessons}>
          <CardBody>
            <Row>
              <Col md="12">
                {lessons.map((lesson, index) => (
                  <div key={lesson.id} className="lesson-timeline-item d-flex">
                    {/* left timeline index */}
                    <div className="timeline-left">
                      <div
                        className={`timeline-number timeline-bg-${
                          LessonTypeColor[lesson.type]
                        } text-${LessonTypeColor[lesson.type]}`}
                      >
                        {index + 1}
                      </div>

                      {index !== lessons.length - 1 && (
                        <div className="timeline-line"></div>
                      )}
                    </div>

                    <Card className="lesson-card mb-3 p-3 w-75">
                      <div className="d-flex justify-content-between align-items-start">
                        {/* lesson content */}
                        <div className="w-100">
                          <div className="d-flex align-items-center flex-wrap">
                            <strong>{lesson.title}</strong>
                            <i
                              id={`lessonIcon-${lesson.id}`}
                              className={`fa ${LESSON_TYPE_META[lesson.type]?.icon || "fa fa-file-pdf"} fa-lg ml-3 mr-2 lesson-icon cursor`}
                              onClick={() => openLessonPreview(lesson)}
                            />

                            <Popover
                              placement="top"
                              isOpen={hoverPopovers === lesson.id}
                              target={`lessonIcon-${lesson.id}`}
                              toggle={() => toggleLessonPopover(lesson.id)}
                              trigger="hover"
                              className="shadow-lg border-0"
                              popperClassName="svp-certificate-popover"
                              container="body"
                              modifiers={{
                                preventOverflow: {
                                  enabled: true,
                                  boundariesElement: "viewport",
                                  padding: 8,
                                },
                                flip: {
                                  enabled: true,
                                  behavior: ["right"],
                                },
                              }}
                            >
                              <PopoverBody>
                                Click icon to preview lesson
                              </PopoverBody>
                            </Popover>

                            <span className="text-muted ml-5">
                              {lesson.duration}
                            </span>
                          </div>
                        </div>

                        {/* edit button */}
                        <Button
                          color="link"
                          size="sm"
                          className="lesson-edit-btn text-primary"
                          onClick={() => handleEditLesson(lesson)}
                        >
                          <i className="fa fa-edit mr-1" />
                          Edit
                        </Button>
                      </div>
                      <hr className="my-3" />

                      {/* description */}
                      <p className="mb-0">{lesson.description}</p>
                    </Card>
                  </div>
                ))}
                <div className="d-flex justify-content-start">
                  <div className="add-more-file-wrapper add-more-file-wrapper--deep-blue">
                    +
                  </div>
                  <Button
                    color="primary"
                    size="sm"
                    className="btn-deep-blue ml-2"
                    onClick={openAddModal}
                  >
                    Add Lesson
                  </Button>
                </div>
              </Col>
            </Row>
          </CardBody>
        </Collapse>
      </Card>
      <RightPanelCard isOpen={isModalOpen !== null} onClose={closeAddModal}>
        <Card className="location-card  mb-0">
          <CardHeader tag="div" className="p-3">
            <div className="d-flex align-items-center justify-content-between mt-2">
              <div className="d-flex align-items-center">
                <i
                  className="fa-solid fa-angle-left cursor mr-2"
                  onClick={closeAddModal}
                ></i>
                <h4 className="mb-0">
                  {isModalOpen === "edit" ? "Edit Lesson" : "Add Lesson"}
                </h4>
              </div>

              <div className="d-flex justify-content-end">
                <i
                  className="fa-solid fa-xmark cursor text-muted"
                  onClick={closeAddModal}
                ></i>
              </div>
            </div>
          </CardHeader>

          <CardBody className="scrollbar-inner overflow-auto">
            <Form>
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label>Title</Label>
                    <span className="text-danger"> *</span>
                    <Input
                      name="title"
                      type="text"
                      placeholder="Enter lesson title"
                      value={lessonFormData.title.value}
                      onChange={(e) =>
                        handleInputChange("title", e.target.value)
                      }
                      onBlur={(e) =>
                        handleOnBlur(e.target.name, e.target.value)
                      }
                      className={lessonFormData.title.error ? "is-invalid" : ""}
                    />
                    {lessonFormData.title.error && (
                      <div className="invalid-feedback d-block">
                        {lessonFormData.title.error}
                      </div>
                    )}
                  </FormGroup>
                </Col>

                <Col md="6">
                  <FormGroup>
                    <Label>Content Sequence</Label>
                    <span className="text-danger"> *</span>
                    <Input
                      name="sequence"
                      type="text"
                      min="1"
                      placeholder="Enter content sequence eg: 1"
                      value={lessonFormData.sequence.value}
                      onChange={(e) =>
                        handleInputChange("sequence", e.target.value)
                      }
                      onBlur={(e) => handleOnBlur("sequence", e.target.value)}
                      className={
                        lessonFormData.sequence.error ? "is-invalid" : ""
                      }
                    />
                    {lessonFormData.sequence.error && (
                      <div className="invalid-feedback d-block">
                        {lessonFormData.sequence.error}
                      </div>
                    )}
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label>Select File Type</Label>
                    <span className="text-danger"> *</span>
                    <Select
                      options={COURSE_TYPE}
                      placeholder="Select type"
                      styles={SELECT_CUSTOM_STYLES}
                      classNamePrefix="react-select"
                      value={
                        lessonFormData.courseType.value
                          ? {
                              value: lessonFormData.courseType.value,
                              label: lessonFormData.courseType.value,
                            }
                          : null
                      }
                      onChange={(selected) =>
                        handleInputChange("courseType", selected.value)
                      }
                      className={
                        lessonFormData.courseType.error ? "is-invalid" : ""
                      }
                      onBlur={(e) => handleOnBlur("courseType", e.target.value)}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                    {lessonFormData.courseType.error && (
                      <div className="invalid-feedback d-block">
                        {lessonFormData.courseType.error}
                      </div>
                    )}
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label>Duration</Label>
                    <span className="text-danger"> *</span>
                    <Input
                      name="duration"
                      type="text"
                      placeholder="Enter lesson duration eg : 5min"
                      value={lessonFormData.duration.value}
                      onChange={(e) =>
                        handleInputChange("duration", e.target.value)
                      }
                      onBlur={(e) => handleOnBlur("duration", e.target.value)}
                      className={
                        lessonFormData.duration.error ? "is-invalid" : ""
                      }
                    />
                    {lessonFormData.duration.error && (
                      <div className="invalid-feedback d-block">
                        {lessonFormData.duration.error}
                      </div>
                    )}
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md="12">
                  <FormGroup>
                    <Label>Description</Label>
                    <span className="text-danger"> *</span>
                    <div className="quill-wrapper">
                      <ReactQuill
                        theme="snow"
                        modules={MODULES}
                        placeholder="Enter Description"
                        value={lessonFormData.descriptions.value}
                        onChange={(value) =>
                          handleInputChange("descriptions", value)
                        }
                        onBlur={() =>
                          handleOnBlur(
                            "descriptions",
                            lessonFormData.descriptions.value,
                          )
                        }
                        className={
                          lessonFormData.descriptions.error ? "is-invalid" : ""
                        }
                      />
                      {lessonFormData.descriptions.error && (
                        <div className="invalid-feedback d-block">
                          {lessonFormData.descriptions.error}
                        </div>
                      )}
                    </div>
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col md="12">
                  {/* File Upload */}
                  <FormGroup className="d-flex flex-column align-items-start w-100">
                    <Label>
                      Upload File <span className="text-danger"> *</span>
                    </Label>

                    <div
                      className={`product-image-upload w-100 p-3 border rounded text-center ${
                        lessonFormData.files.error ? "is-invalid" : ""
                      }`}
                      onDragOver={(e) => e.preventDefault()}
                      onDragEnter={(e) => e.preventDefault()}
                      onDrop={handleDrop}
                    >
                      {lessonFormData.files.value.length > 0 ? (
                        <div className="d-flex flex-wrap justify-content-start align-items-center">
                          {lessonFormData.files.value.map((item, index) => (
                            <div
                              key={index}
                              className="image-preview-wrapper mr-2 position-relative text-center"
                            >
                              {item.type === "application/pdf" ? (
                                <div className="preview-wrapper">
                                  <i className="fa fa-file-pdf text-muted fa-4x"></i>
                                </div>
                              ) : item.type.startsWith("video/") ? (
                                <div className="preview-wrapper">
                                  <i className="fa fa-file-VIDEO text-primary fa-4x"></i>
                                </div>
                              ) : (
                                <img
                                  src={item.preview}
                                  alt={`file-${index}`}
                                  className="product-preview-img"
                                />
                              )}

                              <span
                                className="remove-image position-absolute"
                                onClick={() => handleRemoveFile(index)}
                              >
                                &times;
                              </span>

                              <p
                                className="file-name mt-2 text-truncate"
                                title={item.name}
                              >
                                {item.name}
                              </p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="d-flex flex-column align-items-center">
                          <i className="fa fa-cloud-upload fa-2x text-muted mb-2"></i>
                          <p className="mb-1 text-muted">
                            Drag and Drop files or Browse
                          </p>

                          <Label
                            for="lessonFile"
                            className="choose-image-btn btn btn-simple btn-sm mt-2"
                          >
                            Choose File
                          </Label>
                        </div>
                      )}

                      <Input
                        type="file"
                        name="image"
                        accept="image/*, application/pdf, video/* ,application/vnd.ms-powerpoint,"
                        onChange={handleInputFileChange}
                        className="d-none"
                        id="lessonFile"
                      />
                      {lessonFormData.files.error && (
                        <div className="invalid-feedback d-block">
                          {lessonFormData.files.error}
                        </div>
                      )}
                    </div>
                  </FormGroup>
                </Col>
              </Row>
            </Form>
          </CardBody>
          <CardFooter>
            <div className="d-flex justify-content-end gap-2 mb-3">
              <Button color="secondary" type="button" onClick={closeAddModal}>
                Cancel
              </Button>

              <Button
                className="btn-deep-blue"
                type="button"
                onClick={handleSave}
              >
                {isModalOpen === "edit" ? "Update" : "Save"}
              </Button>

              {isModalOpen === "edit" && (
                <Button
                  color="primary"
                  className="btn-vivid-red"
                  type="button"
                  onClick={() => handleDeleteLesson(lessonFormData.title.value)} // while API integrate use id
                >
                  Delete
                </Button>
              )}
            </div>
          </CardFooter>
        </Card>
      </RightPanelCard>

      <Modal
        isOpen={preview.open}
        toggle={closePreview}
        centered
        size="xl"
        className="lesson-preview-modal"
      >
        <ModalHeader toggle={closePreview}>
          {preview.file?.name || "Preview"}
        </ModalHeader>

        <ModalBody className="lesson-preview-modal-body">
          {preview.file &&
            (() => {
              switch (preview.file.previewType) {
                case "video":
                  return (
                    <div className="video-container">
                      <video controls>
                        <source src={preview.file.preview} />
                      </video>
                    </div>
                  );

                case "pdf":
                  return (
                    <div className="pdf-container">
                      <iframe src={preview.file.preview} title="PDF Preview" />
                    </div>
                  );

                case "image":
                  return (
                    <div className="image-container">
                      <img src={preview.file.preview} alt="Preview" />
                    </div>
                  );

                case "ppt":
                  return (
                    <div className="ppt-container">
                      <iframe src={preview.file.preview} title="PPT Preview" />
                    </div>
                  );

                default:
                  return <p>No preview available</p>;
              }
            })()}
        </ModalBody>
      </Modal>
    </>
  );
};

export default LessonsSection;
